import {
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Switch,
} from '@mui/material';
import {
  LicenseDto,
  LicensePerDeviceDto,
  LicensePerReadingDto,
  LicensePerSensorDto,
  LicenseRequest,
} from '@thingslog/repositories';
import { LicenseType } from '@thingslog/repositories/src/license/LicenseType';
import { LocalizedDatePicker } from '@thingslog/ui-components';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import LicenseMaxCount from '../../../components/LicenseMaxCount/LicenseMaxCountTextField';

import { useTranslation } from 'react-i18next';
import { isAfter } from 'date-fns';
import LicenseValidator from '../../../common/LicenseValidator';
import { TimePeriodSelector } from './TimePeriodSelector';

const AddLicenseModal: FC<AddLicenseModalProps> = ({
  companyName,
  addLicense,
  licenses,
}: AddLicenseModalProps) => {
  const { t } = useTranslation();

  enum DurationType {
    PERIOD = 'PERIOD',
    DATES = 'DATES',
  }

  const [licenseType, setLicenseType] = useState<LicenseType>(LicenseType.PER_DEVICE);
  const [fromDate, setFromDate] = useState<Date | null>(new Date());
  const [toDate, setToDate] = useState<Date | null>(null);
  const [maxReadings, setMaxReadings] = useState<number>(1);
  const [maxDataLoggers, setMaxDataLoggers] = useState<number>(1);
  const [maxSensors, setMaxSensors] = useState<number>(1);
  const [isPrepaid, setIsPrepaid] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [conflictingLicenseDetected, setConflictingLicenseDetected] = useState<boolean>(false);
  const [validityPeriod, setValidityPeriod] = useState<string | null>(null);
  const [durationType, setDurationType] = useState(DurationType.PERIOD);

  useEffect(() => {
    if (licenses) {
      // Check for conflicting date ranges for specific license types (POC, Remote Node)
      if (
        fromDate &&
        toDate &&
        (licenseType === LicenseType.PER_POC || licenseType === LicenseType.PER_REMOTE_NODE)
      ) {
        if (LicenseValidator.detectLicenseConflict(licenses, fromDate, toDate)) {
          setConflictingLicenseDetected(true);
        } else {
          setConflictingLicenseDetected(false);
        }
      } else setConflictingLicenseDetected(false);
    }
  }, [licenseType, licenses, fromDate, toDate]);

  const handleLicenseTypeChange = (event: SelectChangeEvent): void => {
    const type = event.target.value as LicenseType;
    setLicenseType(type);
    if (type === LicenseType.PER_READING && durationType === DurationType.PERIOD) {
      setFromDate(null);
      setToDate(null);
    } else {
      setFromDate(new Date());
      setValidityPeriod(null);
    }
  };

  const handleFromDateChange = (newDate: Date | null): void => {
    if (newDate) {
      setFromDate(newDate);
    }
  };

  const handleToDateChange = (newDate: Date | null): void => {
    if (newDate) {
      setToDate(newDate);
    }
  };

  const handleLicenseValidityPeriodChange = (period: string): void => {
    setValidityPeriod(period);
    if (fromDate || toDate) {
      setFromDate(null);
      setToDate(null);
    }
  };

  const handleMaxCountChange = (licenseType: LicenseType, value: number): void => {
    switch (licenseType) {
      case LicenseType.PER_READING:
        setMaxReadings(value);
        break;
      case LicenseType.PER_DEVICE:
        setMaxDataLoggers(value);
        break;
      case LicenseType.PER_SENSOR:
        setMaxSensors(value);
        break;
      default:
        break;
    }
  };

  const normalizeToUTCDateOnly = (date: Date | null): Date | null => {
    if (!date) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  };

  const handleSubmit = (): void => {
    const licenseData: LicenseRequest = {
      companyName: companyName,
      '@type': licenseType,
      isActive: true,
      validityPeriodFrom: normalizeToUTCDateOnly(fromDate),
      validityPeriodTo: normalizeToUTCDateOnly(toDate),
      isPrepaid: isPrepaid,
    };

    switch (licenseType) {
      case LicenseType.PER_DEVICE:
        (licenseData as LicensePerDeviceDto).maxDataLoggers = maxDataLoggers;
        break;
      case LicenseType.PER_READING:
        const data = licenseData as LicensePerReadingDto;
        data.maxReadings = maxReadings;
        data.validityPeriod = validityPeriod ?? null;
        data.validityPeriodFrom = normalizeToUTCDateOnly(fromDate);
        data.validityPeriodTo = normalizeToUTCDateOnly(toDate);
        break;
      case LicenseType.PER_SENSOR:
        (licenseData as LicensePerSensorDto).maxSensors = maxSensors;
        break;
    }

    addLicense(licenseData);
  };

  const isSubmitButtonDisabled = (
    fromDate: Date | null,
    toDate: Date | null,
    hasError: boolean,
    conflictingLicenseDetected: boolean,
    validityPeriod: string | null
  ): boolean => {
    if (hasError) return true;

    if (conflictingLicenseDetected) return true;

    if (fromDate && toDate && isAfter(fromDate, toDate)) {
      return true;
    }

    if (!(fromDate && toDate) && !validityPeriod) return true;

    return false;
  };

  const handleDurationTypeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const selected = e.target.value as DurationType;
    setDurationType(selected);
    if (selected === DurationType.PERIOD) {
      setFromDate(null);
      setToDate(null);
    } else {
      setValidityPeriod(null);
      setFromDate(new Date());
    }
  };

  const handleIsPrepaidChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setIsPrepaid((prev: boolean) => !prev);
  };

  return (
    <div className="space-y-5 w-[600px]">
      <FormControl fullWidth>
        <InputLabel>{t('license_table_license_type')}</InputLabel>
        <Select
          variant="outlined"
          label={t('license_table_license_type')}
          value={licenseType}
          fullWidth
          size="small"
          onChange={handleLicenseTypeChange}
        >
          <MenuItem value={LicenseType.PER_DEVICE}>
            {t('company_manager_license_per_device')}
          </MenuItem>
          <MenuItem value={LicenseType.PER_READING}>
            {t('company_manager_license_per_reading')}
          </MenuItem>
          <MenuItem value={LicenseType.PER_SENSOR}>
            {t('company_manager_license_per_sensor')}
          </MenuItem>
          <MenuItem value={LicenseType.PER_POC}>{t('company_manager_license_per_poc')}</MenuItem>
          <MenuItem value={LicenseType.PER_REMOTE_NODE}>
            {t('company_manager_license_per_remote_node')}
          </MenuItem>
        </Select>
      </FormControl>

      {licenseType === LicenseType.PER_READING && (
        <FormControl component="fieldset" className="w-full">
          <RadioGroup row value={durationType} onChange={handleDurationTypeChange}>
            <FormControlLabel
              value={DurationType.DATES}
              control={<Radio size="small" />}
              label={t('license_date_range') || 'Date range'}
            />
            <FormControlLabel
              value={DurationType.PERIOD}
              control={<Radio size="small" />}
              label={t('license_validity_period') || 'Validity period'}
            />
          </RadioGroup>
        </FormControl>
      )}

      {(licenseType !== LicenseType.PER_READING || durationType === DurationType.DATES) && (
        <>
          <LocalizedDatePicker
            label={t('profile_page_valid_from')}
            value={fromDate}
            onChange={handleFromDateChange}
            inputFormat="dd/MM/yyyy"
            maxDate={toDate ?? undefined}
            minDate={licenseType === LicenseType.PER_READING ? new Date() : undefined}
            renderInputTextField={{
              size: 'small',
              className: 'bg-white, w-full',
            }}
          />
          <LocalizedDatePicker
            label={t('profile_page_valid_to')}
            value={toDate}
            onChange={handleToDateChange}
            inputFormat="dd/MM/yyyy"
            minDate={fromDate!}
            renderInputTextField={{
              size: 'small',
              className: 'bg-white, w-full',
            }}
          />
        </>
      )}

      {licenseType === LicenseType.PER_READING && durationType === DurationType.PERIOD && (
        <TimePeriodSelector onChange={handleLicenseValidityPeriodChange} />
      )}

      {licenseType !== LicenseType.PER_POC && licenseType !== LicenseType.PER_REMOTE_NODE && (
        <LicenseMaxCount
          onChange={handleMaxCountChange}
          licenseType={licenseType}
          maxSensors={maxSensors}
          maxDataLoggers={maxDataLoggers}
          maxReadings={maxReadings}
          disabled={false}
          hasError={hasError}
          setHasError={setHasError}
        />
      )}

      <FormControlLabel
        control={<Switch checked={isPrepaid} onChange={handleIsPrepaidChange} />}
        label={t('license_table_prepaid')}
        labelPlacement="start"
        sx={{
          margin: 0,
          width: '100%',
          justifyContent: 'space-between',
        }}
      />

      {conflictingLicenseDetected && (
        <div className="text-red-400 text-center">{t('license_conflicting_license_date')}</div>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        className="w-full"
        disabled={isSubmitButtonDisabled(
          fromDate,
          toDate,
          hasError,
          conflictingLicenseDetected,
          validityPeriod
        )}
      >
        {t('button_create')}
      </Button>
    </div>
  );
};

interface AddLicenseModalProps {
  companyName: string;
  addLicense: (body: LicenseRequest) => void;
  licenses: LicenseDto[] | undefined;
}

export default AddLicenseModal;
